import { useEffect, useMemo, useState } from 'react';

import { useMovementConcepts } from '../../../hooks/useMovementConcepts';
import { useAccountingAccounts } from '../../../hooks/useAccountingAccounts';
import {
  useCreateSupplierInvoice,
  useUpdateSupplierInvoice,
  useUploadSupplierInvoiceAttachment,
} from '../../../hooks/useSupplierLedger';
import { invoiceSections } from '../constants/supplierLedgerUiConstants';
import {
  buildAmountLinesFromInvoice,
  buildInvoiceFormFromInvoice,
  calculateAmountLine,
  createAmountLine,
  emptyInvoiceForm,
  hasGeneratedPaymentOrder,
} from '../utils/supplierLedgerHelpers';
import { parseNumber } from '../utils/supplierLedgerFormatters';

const useSupplierInvoiceDialogController = ({
  invoiceToEdit,
  onClose,
  open,
  providers,
}) => {
  const [form, setForm] = useState(emptyInvoiceForm);
  const [amountLines, setAmountLines] = useState(() => [createAmountLine()]);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState(invoiceSections[0].id);

  const createInvoice = useCreateSupplierInvoice();
  const updateInvoice = useUpdateSupplierInvoice();
  const uploadAttachment = useUploadSupplierInvoiceAttachment();
  const { data: movementConcepts = [], isLoading: movementConceptsLoading } = useMovementConcepts();
  const { data: accountingAccounts = [], isLoading: accountingAccountsLoading } = useAccountingAccounts();

  const isEditMode = Boolean(invoiceToEdit?.uuid);
  const currentAttachmentName = invoiceToEdit?.attachments?.[0]?.original_filename || '';
  const locksFinancialFields = isEditMode && (
    hasGeneratedPaymentOrder(invoiceToEdit) || Number(invoiceToEdit?.paid_amount || 0) > 0
  );

  const providerOptions = useMemo(
    () => providers.map((provider) => ({
      value: provider.uuid,
      label: provider.legal_business_name || provider.company || provider.cuit || 'Proveedor sin nombre',
    })),
    [providers],
  );

  const selectedProviderName = useMemo(
    () => providerOptions.find((provider) => provider.value === form.uuid_legal_entity)?.label || invoiceToEdit?.provider_name || '',
    [form.uuid_legal_entity, invoiceToEdit?.provider_name, providerOptions],
  );

  const movementConceptOptions = useMemo(() => {
    const options = movementConcepts
      .map((concept) => ({
        value: concept.uuid,
        label: concept.name,
        code: concept.code,
        description: concept.description,
      }))
      .sort((left, right) => String(left.label || '').localeCompare(String(right.label || '')));

    // Importes guardados con un concepto que ya no esta activo: se muestran para no perder el dato,
    // pero hay que reemplazarlos antes de guardar.
    const currentConcepts = amountLines
      .filter((line) => line.uuid_movement_concept && !options.some((option) => option.value === line.uuid_movement_concept))
      .map((line) => ({
        value: line.uuid_movement_concept,
        label: line.movement_concept_label || 'Concepto anterior',
        code: '',
        description: 'Concepto anterior fuera del catálogo activo',
        legacy: true,
      }));

    return [...currentConcepts, ...options];
  }, [amountLines, movementConcepts]);

  const accountingAccountOptions = useMemo(() => {
    const options = accountingAccounts
      .filter((account) => account.is_active !== false)
      .map((account) => ({
        ...account,
        value: account.uuid,
        label: account.code ? `${account.code} - ${account.name}` : account.name,
      }))
      .sort((left, right) => String(left.label || '').localeCompare(String(right.label || '')));

    const currentAccounts = amountLines
      .filter((line) => line.uuid_accounting_account && !options.some((option) => option.value === line.uuid_accounting_account))
      .map((line) => ({
        value: line.uuid_accounting_account,
        label: line.accounting_account_label || 'Cuenta anterior fuera del catálogo activo',
        legacy: true,
      }));

    return [...currentAccounts, ...options];
  }, [accountingAccounts, amountLines]);

  const amountTotals = useMemo(() => {
    const baseTotals = amountLines.reduce(
      (acc, line) => {
        const lineAmounts = calculateAmountLine(line);
        acc.gross += lineAmounts.gross;
        acc.net += lineAmounts.net;
        acc.vat += lineAmounts.vat;
        return acc;
      },
      { gross: 0, net: 0, vat: 0 },
    );
    const perceptions = parseNumber(form.vat_perception_amount) + parseNumber(form.gross_income_perception_amount);

    return {
      ...baseTotals,
      perceptions,
      total: baseTotals.gross + perceptions,
    };
  }, [amountLines, form.gross_income_perception_amount, form.vat_perception_amount]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAmountLineChange = (lineId, field, value) => {
    setAmountLines((prev) => prev.map((line) => (
      line.id === lineId
        ? (() => {
          const next = { ...line, [field]: value };
          if (field === 'cost_nature') next.cost_traceability = value;
          if (field === 'cost_traceability') next.cost_nature = value;
          if (field === 'uuid_movement_concept') {
            next.movement_concept_label = movementConcepts.find((concept) => concept.uuid === value)?.name || '';
          }
          if (field === 'uuid_accounting_account') {
            const account = accountingAccounts.find((item) => item.uuid === value);
            next.accounting_account_label = account?.name || '';
            next.cost_behavior = account?.default_cost_behavior || next.cost_behavior;
            next.cost_nature = account?.default_cost_nature || next.cost_nature;
            next.cost_traceability = account?.default_cost_nature || next.cost_traceability;
            next.cost_area = account?.default_cost_area || next.cost_area;
            if (account?.default_uuid_movement_concept) {
              next.uuid_movement_concept = account.default_uuid_movement_concept;
              next.movement_concept_label = account.default_movement_concept_name || next.movement_concept_label;
            }
          }
          return next;
        })()
        : line
    )));
  };

  const handleAddAmountLine = () => {
    setAmountLines((prev) => [...prev, createAmountLine()]);
  };

  const handleRemoveAmountLine = (lineId) => {
    setAmountLines((prev) => {
      if (prev.length === 1) return [createAmountLine()];
      return prev.filter((line) => line.id !== lineId);
    });
  };

  useEffect(() => {
    if (!open) return;

    if (invoiceToEdit) {
      setForm(buildInvoiceFormFromInvoice(invoiceToEdit));
      setAmountLines(buildAmountLinesFromInvoice(invoiceToEdit));
    } else {
      setForm(emptyInvoiceForm());
      setAmountLines([createAmountLine()]);
    }
    setFile(null);
    setError('');
    setActiveSection(invoiceSections[0].id);
  }, [invoiceToEdit, open]);

  const handleClose = () => {
    setForm(emptyInvoiceForm());
    setAmountLines([createAmountLine()]);
    setFile(null);
    setError('');
    setActiveSection(invoiceSections[0].id);
    onClose();
  };

  const buildPayload = () => {
    const payload = {
      ...form,
      due_date: form.due_date || null,
      exchange_rate_to_ars: form.currency === 'USD' ? parseNumber(form.exchange_rate_to_ars) : null,
      net_amount: amountTotals.net,
      vat_amount: amountTotals.vat,
      vat_perception_amount: parseNumber(form.vat_perception_amount),
      gross_income_perception_amount: parseNumber(form.gross_income_perception_amount),
      total_amount: amountTotals.total,
      items: amountLines
        .map((line, index) => {
          const lineAmounts = calculateAmountLine(line);
          const costNature = line.cost_nature || line.cost_traceability || null;
          return {
            line_number: index + 1,
            description: line.description || null,
            uuid_movement_concept: line.uuid_movement_concept || null,
            uuid_accounting_account: line.uuid_accounting_account || null,
            accounting_account_label: line.accounting_account_label || null,
            cost_behavior: line.cost_behavior || null,
            cost_nature: costNature,
            cost_traceability: costNature,
            cost_area: line.cost_area || null,
            net_amount: lineAmounts.net,
            vat_rate: lineAmounts.vatRate,
            vat_amount: lineAmounts.vat,
            gross_amount: lineAmounts.gross,
          };
        })
        .filter((line) => line.gross_amount > 0),
      tax_lines: amountLines
        .map((line) => {
          const lineAmounts = calculateAmountLine(line);
          return {
            tax_type: 'IVA',
            rate: lineAmounts.vatRate,
            taxable_base_amount: lineAmounts.net,
            amount: lineAmounts.vat,
          };
        })
        .filter((line) => line.taxable_base_amount > 0 || line.amount > 0),
    };

    if (isEditMode) {
      delete payload.uuid_legal_entity;
      delete payload.duplicate_override_reason;
      delete payload.status;
      return payload;
    }

    return {
      ...payload,
      duplicate_override_reason: form.duplicate_override_reason || null,
      status: 'open',
    };
  };

  const handleSubmit = async () => {
    setError('');
    const lineWithoutActiveConcept = amountLines.find((line) => {
      if (calculateAmountLine(line).gross <= 0) return false;
      const selectedConcept = movementConceptOptions.find((option) => option.value === line.uuid_movement_concept);
      return !selectedConcept || selectedConcept.legacy;
    });
    if (lineWithoutActiveConcept) {
      const lineNumber = amountLines.indexOf(lineWithoutActiveConcept) + 1;
      setError(`El importe ${lineNumber} debe tener un concepto activo del catálogo de conceptos de movimiento.`);
      setActiveSection('amounts');
      return;
    }
    if (amountTotals.total <= 0) {
      setError('Debe cargar al menos un importe mayor a cero.');
      setActiveSection('amounts');
      return;
    }
    try {
      const savedInvoice = isEditMode
        ? await updateInvoice.mutateAsync({ id: invoiceToEdit.uuid, data: buildPayload() })
        : await createInvoice.mutateAsync(buildPayload());
      if (file) {
        await uploadAttachment.mutateAsync({ invoiceId: savedInvoice.uuid, file });
      }
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    }
  };

  return {
    activeSection,
    amountLines,
    amountTotals,
    accountingAccountOptions,
    accountingAccountsLoading,
    currentAttachmentName,
    error,
    file,
    form,
    handleAddAmountLine,
    handleAmountLineChange,
    handleChange,
    handleClose,
    handleRemoveAmountLine,
    handleSubmit,
    isEditMode,
    isSaving: createInvoice.isPending || updateInvoice.isPending || uploadAttachment.isPending,
    locksFinancialFields,
    movementConceptOptions,
    movementConceptsLoading,
    providerOptions,
    selectedProviderName,
    setActiveSection,
    setFile,
  };
};

export default useSupplierInvoiceDialogController;
